import { gql } from '@apollo/client';

// Powers the per-SSI summary dashboard — plans 314 + 316.
// One query fetches KPIs + monthly series + aging + status mix +
// operator-focused fields (waterfall, cycle time, velocity, top-5s,
// collection rate, delinquency scatter, pareto).
export const GET_SSI_FINANCIAL_SUMMARY_QUERY = gql`
  query GetSsiFinancialSummary($ssiId: UUID!, $monthsBack: Int = 12) {
    ssiFinancialSummary(ssiId: $ssiId, monthsBack: $monthsBack) {
      ssiId
      monthsBack
      totalInvoicedMicros
      totalCashReceivedMicros
      appliedToInvoicesMicros
      outstandingArMicros
      totalInvoiceCount
      openInvoiceCount
      oldestUnpaidDays
      monthlySeries {
        month
        invoicedMicros
        cashReceivedMicros
        invoiceCount
        remittanceCount
      }
      aging {
        bucket
        invoiceCount
        outstandingMicros
      }
      countsByStatus {
        status
        count
        totalMicros
      }
      unbilledBacklogMicros
      velocity {
        serviceToInvoiceDays
        trioToInvoiceDays
        sentToPaidDays
      }
      waterfall {
        earnedMicros
        unbilledWipMicros
        invoicedOpenMicros
        cashMicros
        withheldMicros
      }
      cycleTimeStack {
        visitToTrioDays
        trioToInvoiceDays
        sentToPaidDays
      }
      topOverdueInvoices {
        id
        invoiceNumber
        invoiceDate
        derivedDueDate
        outstandingMicros
        daysPastDue
        ageDays
        sponsorName
        protocol
      }
      topOldestUnbilledVisits {
        activityId
        activityName
        visitName
        serviceDate
        patientNumber
        obligationMicros
        daysSinceVisit
      }
      collectionRateByCategory {
        category
        aliCount
        obligationMicros
        invoicedMicros
        cashMicros
      }
      arDelinquencyScatter {
        invoiceId
        invoiceNumber
        outstandingMicros
        daysPastDue
        sponsorName
      }
      pareto {
        bucket
        dollars
      }
      paymentVelocity {
        avgDaysToPaymentDays
        avgServiceToInvoiceDays
        avgDaysUnpaidDays
        distribution {
          bucket
          paidCount
          outstandingCount
        }
      }
    }
  }
`;

export interface SsiMonthlyPoint {
  month: string;
  invoicedMicros: number;
  cashReceivedMicros: number;
  invoiceCount: number;
  remittanceCount: number;
}

export interface SsiAgingBucket {
  bucket: string;
  invoiceCount: number;
  outstandingMicros: number;
}

export interface SsiStatusCount {
  status: string;
  count: number;
  totalMicros: number;
}

export interface SsiVelocityDays {
  serviceToInvoiceDays: number | null;
  trioToInvoiceDays: number | null;
  sentToPaidDays: number | null;
}

export interface SsiWaterfall {
  earnedMicros: number;
  unbilledWipMicros: number;
  invoicedOpenMicros: number;
  cashMicros: number;
  withheldMicros: number;
}

export interface SsiCycleTimeStack {
  visitToTrioDays: number | null;
  trioToInvoiceDays: number | null;
  sentToPaidDays: number | null;
}

export interface SsiOverdueInvoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  derivedDueDate: string | null;
  outstandingMicros: number;
  daysPastDue: number | null;
  ageDays: number;
  sponsorName: string | null;
  protocol: string | null;
}

export interface SsiOldestUnbilledVisit {
  activityId: string;
  activityName: string;
  visitName: string | null;
  serviceDate: string | null;
  patientNumber: string | null;
  obligationMicros: number;
  daysSinceVisit: number;
}

export interface SsiCategoryRate {
  category: string;
  aliCount: number;
  obligationMicros: number;
  invoicedMicros: number;
  cashMicros: number;
}

export interface SsiDelinquencyPoint {
  invoiceId: string;
  invoiceNumber: string;
  outstandingMicros: number;
  daysPastDue: number;
  sponsorName: string | null;
}

export interface SsiParetoBucket {
  bucket: string;
  dollars: number;
}

export interface SsiPaymentDistributionBucket {
  bucket: string;
  paidCount: number;
  outstandingCount: number;
}

export interface SsiPaymentVelocity {
  avgDaysToPaymentDays: number | null;
  avgServiceToInvoiceDays: number | null;
  avgDaysUnpaidDays: number | null;
  distribution: SsiPaymentDistributionBucket[];
}

export interface SsiFinancialSummary {
  ssiId: string;
  monthsBack: number;
  totalInvoicedMicros: number;
  totalCashReceivedMicros: number;
  appliedToInvoicesMicros: number;
  outstandingArMicros: number;
  totalInvoiceCount: number;
  openInvoiceCount: number;
  oldestUnpaidDays: number | null;
  monthlySeries: SsiMonthlyPoint[];
  aging: SsiAgingBucket[];
  countsByStatus: SsiStatusCount[];
  unbilledBacklogMicros: number;
  velocity: SsiVelocityDays;
  waterfall: SsiWaterfall;
  cycleTimeStack: SsiCycleTimeStack;
  topOverdueInvoices: SsiOverdueInvoice[];
  topOldestUnbilledVisits: SsiOldestUnbilledVisit[];
  collectionRateByCategory: SsiCategoryRate[];
  arDelinquencyScatter: SsiDelinquencyPoint[];
  pareto: SsiParetoBucket[];
  paymentVelocity: SsiPaymentVelocity;
}
